export const fmtINR = (n) => {
  // Function name kept for import stability; formats as AED with en-AE conventions.
  if (n === null || n === undefined || Number.isNaN(n)) return "N/A";
  if (n >= 1000000) return `AED ${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `AED ${(n / 1000).toFixed(2)}K`;
  return `AED ${Number(n).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Exact AED  amount with Indian commas and ALWAYS 2 decimals — NO Cr/L/K compaction.
// Use this for every report / table / detail / drill-down amount cell where the
// exact purchase value (paise included) must be shown — never rounded.
export const fmtMoney2 = (n) => {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const sign = v < 0 ? "-" : "";
  return `${sign}AED ${Math.abs(v).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Full AED amount with en-AE commas — no M/K compaction, exact 2 decimals. Used for tooltips/hover.
export const fmtINRFull = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "N/A";
  return `AED ${Number(n).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const fmtNum = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "N/A";
  return Number(n).toLocaleString("en-AE");
};

// Compact unitless number: 2.25M / 12.5K. Used on KPI tiles where space is tight.
export const fmtCompactNum = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "N/A";
  const v = Number(n);
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 1000000) return `${sign}${(abs / 1000000).toFixed(2)}M`;
  if (abs >= 1000)    return `${sign}${(abs / 1000).toFixed(1)}K`;
  return `${sign}${abs.toLocaleString("en-AE", { maximumFractionDigits: 0 })}`;
};

export const fmtPct = (n, digits = 1) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "N/A";
  return `${Number(n).toFixed(digits)}%`;
};

// All date/time rendering is forced to Asia/Dubai (Gulf Standard Time, UTC+4)
// so the platform shows UAE time regardless of the viewer's machine timezone.
const IST_TZ = "Asia/Dubai";

// Tolerant date parser. Handles ISO strings AND the formats some POS terminals send:
//  - year-first "YYYY-MM-DD[ T]HH:MM[:SS][tz]"  (space-separated naive form is what
//    Safari rejects via new Date(); we rebuild it as proper ISO)
//  - day-first  "DD-MM-YYYY / DD/MM/YYYY[ HH:MM[:SS]]"
// A value with NO timezone is treated as IST (+05:30); an explicit tz is preserved.
const parseDate = (s) => {
  if (s === null || s === undefined || s === "") return null;
  const str = String(s).trim();
  // Year-first / ISO: YYYY-MM-DD optionally with time and timezone.
  const iso = str.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?/
  );
  if (iso) {
    const [, y, mo, d, hh = "00", mi = "00", ss = "00", tz] = iso;
    const tzPart = tz ? tz.replace(/([+-]\d{2})(\d{2})$/, "$1:$2") : "+05:30";
    const norm = `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}T${hh.padStart(2, "0")}:${mi}:${ss}${tzPart}`;
    const dt = new Date(norm);
    if (!Number.isNaN(dt.getTime())) return dt;
  }
  // Day-first DD-MM-YYYY / DD/MM/YYYY (optional HH:MM[:SS]) — treated as IST.
  const m = str.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (m) {
    const [, dd, mm, yyyy, hh = "00", mi = "00", ss = "00"] = m;
    const norm = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}T${hh.padStart(2, "0")}:${mi}:${ss}+05:30`;
    const d = new Date(norm);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const fallback = new Date(str);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

export const fmtDate = (s) => {
  const d = parseDate(s);
  if (!d) return s ? s : "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: IST_TZ });
};

export const fmtDateTime = (s) => {
  const d = parseDate(s);
  if (!d) return s ? s : "—";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: IST_TZ });
};

// Year-month-date format in IST: "YYYY-MM-DD HH:MM" (24-hour).
export const fmtDateTimeISO = (s) => {
  const d = parseDate(s);
  if (!d) return s ? s : "—";
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: IST_TZ,
  }).formatToParts(d).reduce((a, p) => ((a[p.type] = p.value), a), {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
};

// Year-month-date in IST: "YYYY-MM-DD" (keeps the report's sortable ISO date format
// but converts to Asia/Dubai so it matches the dashboard — no more off-by-one).
export const fmtDateISO = (s) => {
  const d = parseDate(s);
  if (!d) return s ? s : "—";
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit", timeZone: IST_TZ,
  }).formatToParts(d).reduce((a, p) => ((a[p.type] = p.value), a), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};


export const tierClass = (t) => `pill pill-${(t || "silver").toLowerCase()}`;
